import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { currentLocale } from '../components/numericFormat';
import { EntityLink } from './EntityLink';
import { describeRecord } from '../portfolio-dashboard/referenceLabel';
import { knownEntityType } from './entityTypes';

/**
 * **Jede Entität, angezeigt aus dem Schema von tri-model.**
 *
 * Für die zentralen Typen — Gebäude, Anlage, Flurstück — gibt es eigene Seiten. Für alle anderen
 * (eine Baulast, ein Grundbucheintrag, ein Darlehen) zeigt diese Ansicht, was das Modell über die
 * Klasse weiss: ihre Felder mit Beschriftung und Einheit, ihre Bezüge als Links unter derselben
 * Adresse (`/entities/:type/:id`) und ihre Änderungshistorie. Nur Anzeige — ob man hier auch
 * bearbeiten darf, ist eine spätere Entscheidung.
 */

const EMPTY = '—';

const isEmpty = (value: any) =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

/** Ein Feldwert als Text: Datum, Ja/Nein, Zahl mit Einheit; Strukturen als kurzes JSON. */
export const formatEntityValue = (field: any, value: any, lang: 'de' | 'en'): string => {
    if (isEmpty(value)) return EMPTY;
    const type = field?.type;
    if (type === Date || value instanceof Date) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString(currentLocale());
    }
    if (type === Boolean || typeof value === 'boolean') return value ? (lang === 'en' ? 'Yes' : 'Ja') : lang === 'en' ? 'No' : 'Nein';
    if (typeof value === 'number') return `${value.toLocaleString(currentLocale())}${field?.unit ? ` ${field.unit}` : ''}`;
    if (typeof value === 'object') {
        const text = JSON.stringify(value);
        return text.length > 160 ? `${text.slice(0, 157)}…` : text;
    }
    return `${value}${field?.unit ? ` ${field.unit}` : ''}`;
};

/**
 * Ein Feldwert, wie ein Mensch ihn liest: ein Werttyp als „Bezeichnung Wert · …", eine Liste
 * Eintrag für Eintrag, eine Referenz in einem Werttyp (`Field.target`) als Link.
 */
export function EntityValue({ field, value, lang }: { field: any; value: any; lang: 'de' | 'en' }) {
    if (isEmpty(value)) return <>{EMPTY}</>;
    if (field?.multiple && Array.isArray(value)) {
        return (
            <ul className="space-y-0.5">
                {value.map((entry: any, index: number) => (
                    <li key={index}>
                        <EntityValue field={{ ...field, multiple: false }} value={entry} lang={lang} />
                    </li>
                ))}
            </ul>
        );
    }
    if (field?.target && value && typeof value === 'object' && value._id) {
        return <EntityLink reference={{ _id: String(value._id), type: String(value.type ?? field.target) }} />;
    }
    const inner = field?.type?.fields as any[] | undefined;
    if (inner && value && typeof value === 'object') {
        const shown = inner.filter((sub) => !isEmpty(value[sub.name]));
        if (shown.length === 0) return <>{EMPTY}</>;
        return (
            <span className="inline-flex flex-wrap gap-x-2">
                {shown.map((sub) => (
                    <span key={sub.name}>
                        <span className="text-muted-foreground">{sub.label?.[lang] ?? sub.label?.de ?? sub.name}</span>{' '}
                        <EntityValue field={sub} value={value[sub.name]} lang={lang} />
                    </span>
                ))}
            </span>
        );
    }
    return <>{formatEntityValue(field, value, lang)}</>;
}

/** Die Referenzen eines Bezugs: eine, mehrere, als Objekt `{ _id }` oder als blanke Id. */
const referencesOf = (relationship: any, value: any): Array<{ _id: string; type: string }> => {
    const typeName = relationship?.typeName ?? relationship?.type?.type?.name ?? relationship?.getTypeName?.() ?? 'Entity';
    const one = (entry: any) => {
        if (!entry) return null;
        if (typeof entry === 'string') return { _id: entry, type: typeName };
        if (entry._id) return { _id: String(entry._id), type: String(entry.type ?? entry._type ?? entry.constructor?.type?.name ?? typeName) };
        return null;
    };
    return (Array.isArray(value) ? value : [value]).map(one).filter((entry): entry is { _id: string; type: string } => !!entry);
};

/** Dieselben Namensfelder wie für Bezüge (referenceLabel) — Überschrift und Link sollen gleich heißen. */
const labelOfEntity = (entity: any): string => describeRecord(entity);

export default function GenericEntityView({ entityType, entity }: { entityType: string; entity: any }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
    const descriptor = knownEntityType(entityType) as any;

    if (!descriptor) {
        return <div className="text-sm text-destructive">{t('portfolioDashboard.entity.unknownType', { type: entityType })}</div>;
    }

    const label = (entry: any) => entry?.label?.[lang] ?? entry?.label?.de ?? entry?.name;
    const fields = ((descriptor.fields ?? []) as any[]).filter((field) => !field.internal && !['dataroom', 'documents', 'changes'].includes(field.name));
    const relationships = (descriptor.relationships ?? []) as any[];
    const changes = (Array.isArray(entity?.changes) ? [...entity.changes] : []).reverse();
    const fieldLabel = (name: string) => label(fields.find((field) => field.name === name)) ?? name;

    return (
        <div className="space-y-6 p-4">
            <header className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-semibold">{labelOfEntity(entity)}</h1>
                    <Badge variant="secondary">{label(descriptor.type)}</Badge>
                    <Badge variant="outline">{t('portfolioDashboard.entity.readOnly')}</Badge>
                </div>
                <div className="font-mono text-xs text-muted-foreground">{String(entity?._id ?? '')}</div>
            </header>

            <section className="space-y-2">
                <h2 className="text-sm font-semibold">{t('portfolioDashboard.entity.fields')}</h2>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[minmax(10rem,16rem)_1fr]">
                    {fields.map((field) => (
                        <div key={field.name} className="contents">
                            <dt className="text-muted-foreground">{label(field)}</dt>
                            <dd className={isEmpty(entity?.[field.name]) ? 'text-muted-foreground' : ''}>
                                <EntityValue field={field} value={entity?.[field.name]} lang={lang} />
                            </dd>
                        </div>
                    ))}
                </dl>
            </section>

            {relationships.length > 0 ? (
                <section className="space-y-2">
                    <h2 className="text-sm font-semibold">{t('portfolioDashboard.entity.relationships')}</h2>
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[minmax(10rem,16rem)_1fr]">
                        {relationships.map((relationship) => {
                            const references = referencesOf(relationship, entity?.[relationship.name]);
                            return (
                                <div key={relationship.name} className="contents">
                                    <dt className="text-muted-foreground">{label(relationship)}</dt>
                                    <dd className="flex flex-wrap gap-x-3 gap-y-1">
                                        {references.length === 0 ? <span className="text-muted-foreground">{EMPTY}</span> : references.map((reference) => <EntityLink key={reference._id} reference={reference} />)}
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>
                </section>
            ) : null}

            <section className="space-y-2">
                <h2 className="text-sm font-semibold">{t('portfolioDashboard.entity.history')}</h2>
                {changes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('portfolioDashboard.entity.noHistory')}</p>
                ) : (
                    <ol className="space-y-2 text-sm">
                        {changes.map((change: any, index: number) => (
                            <li key={index} className="border-l-2 pl-3">
                                <div>
                                    <span className="font-medium">{fieldLabel(change.field)}</span>
                                    {' → '}
                                    <EntityValue field={fields.find((field) => field.name === change.field)} value={change.value} lang={lang} />
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {change.timestamp ? new Date(change.timestamp).toLocaleString(currentLocale()) : ''}
                                    {change.user ? ` · ${change.user}` : change.type === 'system' ? ' · System' : ''}
                                </div>
                                {change.comment ? <div className="text-xs">{change.comment}</div> : null}
                                {change.reference?._id ? <EntityLink reference={change.reference} /> : null}
                            </li>
                        ))}
                    </ol>
                )}
            </section>
        </div>
    );
}
